import { z } from 'zod';

/**
 * `codigo` de erro é uma **string estável**, consumida pelo frontend — não mude
 * sem migrar (CLAUDE.md). Formato de resposta de erro da API (SPEC.md §8):
 *   { erro: { codigo, mensagem, detalhes? } }
 */

/** Motivos de rejeição de indicação — as 8 regras da seção 5.1 do SPEC. */
export const MOTIVOS_REJEICAO_INDICACAO = [
  'codigo_inexistente', // 1 — código não existe
  'codigo_inativo', // 2 — código inativo ou expirado
  'nao_e_cliente_novo', // 3 — indicado já era cliente da Varga
  'mesmo_contato', // 4 — telefone do indicado = do indicador
  'mesmo_documento', // 5 — documento do indicado = do indicador
  'ja_indicado', // 6 — indicado já tem indicação convertida
  'indicador_inativo', // 7 — indicador inativo
  'campanha_encerrada', // 8 — now >= campanha_fim
] as const;

export type MotivoRejeicaoIndicacao = (typeof MOTIVOS_REJEICAO_INDICACAO)[number];

export const motivoRejeicaoIndicacaoSchema = z.enum(MOTIVOS_REJEICAO_INDICACAO);

/**
 * Códigos de erro HTTP da API. Inclui os motivos de rejeição (que também podem
 * voltar como erro de validação síncrona em `POST /codigos/validar`) mais os
 * erros de plataforma.
 */
export const CODIGOS_ERRO = [
  ...MOTIVOS_REJEICAO_INDICACAO,
  'validacao', // corpo/params fora do schema Zod
  'nao_autenticado',
  'nao_autorizado',
  'nao_encontrado',
  'conflito', // ex.: numero_os duplicado com dados divergentes
  'telefone_invalido',
  'documento_invalido',
  'voucher_ja_entregue',
  'colisao_codigo', // não gerou código único após as tentativas
  'rate_limit',
  'interno',
] as const;

export type CodigoErro = (typeof CODIGOS_ERRO)[number];

/**
 * Código extra, **só do parser da OS** (`apps/hermes/parser`) — nunca sai da
 * API. O PDF não deu para ler (sem camada de texto e OCR falhou, 3+ campos
 * ausentes). Fica **fora** de `CODIGOS_ERRO` de propósito: a API mapeia aquele
 * catálogo 1:1 para status HTTP e nunca emite este. Ver `docs/PARSER-OS.md §4`
 * e a tool `parse_os` (`apps/hermes`).
 */
export const CODIGO_ERRO_EXTRACAO_FALHOU = 'extracao_falhou' as const;

/** Catálogo que o MCP server do Hermes usa: os da API + o do parser. */
export const CODIGOS_ERRO_HERMES = [...CODIGOS_ERRO, CODIGO_ERRO_EXTRACAO_FALHOU] as const;
export type CodigoErroHermes = (typeof CODIGOS_ERRO_HERMES)[number];

export const erroApiSchema = z.object({
  erro: z.object({
    codigo: z.enum(CODIGOS_ERRO_HERMES),
    mensagem: z.string(),
    detalhes: z.unknown().optional(),
  }),
});

export type ErroApi = z.infer<typeof erroApiSchema>;

export function erroApi(codigo: CodigoErroHermes, mensagem: string, detalhes?: unknown): ErroApi {
  return detalhes === undefined
    ? { erro: { codigo, mensagem } }
    : { erro: { codigo, mensagem, detalhes } };
}
